import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Award, Users, Target, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const carouselImages = [
  { src: 'https://customer-assets.emergentagent.com/job_7bec0238-1e03-4c93-afc1-e7baf1238116/artifacts/4iqyyie9_01-actuel.png', alt: 'Actuel' },
  { src: 'https://customer-assets.emergentagent.com/job_7bec0238-1e03-4c93-afc1-e7baf1238116/artifacts/oggoq3xl_02-pers%20fut%201.png', alt: 'Perspective future' },
  { src: 'https://customer-assets.emergentagent.com/job_7bec0238-1e03-4c93-afc1-e7baf1238116/artifacts/spwpjavw_03-%C3%A9clat%C3%A9%20int.png', alt: 'Vue éclatée intérieure' },
  { src: 'https://customer-assets.emergentagent.com/job_7bec0238-1e03-4c93-afc1-e7baf1238116/artifacts/dk0pw5er_04-coupe%20jour.png', alt: 'Coupe de jour' },
  { src: 'https://customer-assets.emergentagent.com/job_7bec0238-1e03-4c93-afc1-e7baf1238116/artifacts/ybc5blac_05-coupe%20nuit.png', alt: 'Coupe de nuit' },
  { src: 'https://customer-assets.emergentagent.com/job_7bec0238-1e03-4c93-afc1-e7baf1238116/artifacts/1t2w29bo_06-pers%20fut.png', alt: 'Perspective future extérieure' },
  { src: 'https://customer-assets.emergentagent.com/job_7bec0238-1e03-4c93-afc1-e7baf1238116/artifacts/oyd2dmws_07-pers%20transp.jpg', alt: 'Perspective transparence' },
];

const dossierImages = [
  { src: 'https://customer-assets.emergentagent.com/job_7bec0238-1e03-4c93-afc1-e7baf1238116/artifacts/c489cvix_DP01-DP-%20page%20de%20garde%20%281%29.jpg', alt: 'Page de garde' },
  { src: 'https://customer-assets.emergentagent.com/job_7bec0238-1e03-4c93-afc1-e7baf1238116/artifacts/vbw17l3p_DP02-DPC1-%20plan%20situation%20%281%29.jpg', alt: 'Plan de situation' },
  { src: 'https://customer-assets.emergentagent.com/job_7bec0238-1e03-4c93-afc1-e7baf1238116/artifacts/6xr6f38q_DP03-DPC1a-%20plan%20cad-aerien%20%281%29.jpg', alt: 'Plan cadastral - Vue aérienne' },
  { src: 'https://customer-assets.emergentagent.com/job_7bec0238-1e03-4c93-afc1-e7baf1238116/artifacts/z4gcrxpr_DP04-DPC2-%20plan%20masse%20act%20%281%29.jpg', alt: 'Plan masse' },
  { src: 'https://customer-assets.emergentagent.com/job_7bec0238-1e03-4c93-afc1-e7baf1238116/artifacts/ejkiyu5c_DP05-DPC4-%20plan%20toit-fac%20%281%29.jpg', alt: 'Plans façades - Toiture' },
  { src: 'https://customer-assets.emergentagent.com/job_7bec0238-1e03-4c93-afc1-e7baf1238116/artifacts/48oemyrt_DP06-DPC5-%20Aspect%20ext%20%281%29.jpg', alt: 'Aspect extérieur' },
  { src: 'https://customer-assets.emergentagent.com/job_7bec0238-1e03-4c93-afc1-e7baf1238116/artifacts/4873nmyo_DP07-DPC6-%20Doc%20graphique%20%281%29.jpg', alt: 'Document graphique' },
  { src: 'https://customer-assets.emergentagent.com/job_7bec0238-1e03-4c93-afc1-e7baf1238116/artifacts/rrjatb12_DP08-DPC7-8-%20photos%20%281%29.jpg', alt: 'Photographies proche/lointain' },
];

const Presentation = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [currentDossier, setCurrentDossier] = useState(0);
  const [zoomedImage, setZoomedImage] = useState(null);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % carouselImages.length);
  }, []);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + carouselImages.length) % carouselImages.length);
  }, []);

  const nextDossier = useCallback(() => {
    setCurrentDossier((prev) => (prev + 1) % dossierImages.length);
  }, []);

  const prevDossier = useCallback(() => {
    setCurrentDossier((prev) => (prev - 1 + dossierImages.length) % dossierImages.length);
  }, []);

  useEffect(() => {
    const timer = setInterval(nextSlide, 4000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  useEffect(() => {
    const timer = setInterval(nextDossier, 5000);
    return () => clearInterval(timer);
  }, [nextDossier]);

  const values = [
    {
      icon: Award,
      title: 'Excellence',
      description: '25 ans d\'expérience dans le domaine de l\'urbanisme et de l\'architecture'
    },
    {
      icon: Users,
      title: 'Écoute',
      description: 'À l\'écoute de vos envies et exigences pour créer des solutions personnalisées'
    },
    {
      icon: Target,
      title: 'Efficacité',
      description: 'Travail soigné et rapide, sans perte de temps pour vos projets'
    },
    {
      icon: CheckCircle2,
      title: 'Garantie',
      description: 'Forte garantie d\'acceptation grâce au respect des règles d\'urbanisme'
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-pink-50 via-white to-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <div className="flex items-center justify-center gap-4 mb-4">
              <h1 className="text-5xl font-bold text-gray-900">
                URBA-2C, votre partenaire en <span className="text-pink-600">urbanisme</span>
              </h1>
              <video src="https://customer-assets.emergentagent.com/job_7bec0238-1e03-4c93-afc1-e7baf1238116/artifacts/nk3b9zei_logo-U2C.mp4" autoPlay loop muted playsInline className="h-24 w-auto flex-shrink-0 hidden sm:block rounded-lg" />
            </div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Bureau d'études spécialisé dans l'accompagnement de vos projets d'urbanisme
            </p>
            <div className="pt-4" style={{animation: 'pulseScale 2s ease-in-out infinite'}}>
              <Link to="/contact" className="inline-block px-8 py-3 bg-pink-600 hover:bg-pink-700 text-white text-xl font-bold rounded-full shadow-lg hover:shadow-xl transition-all duration-300">
                Etude de faisabilité offerte pour votre projet !!
              </Link>
            </div>
            <style>{`@keyframes pulseScale { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.08); } }`}</style>
          </div>
        </div>
      </section>

      {/* Who We Serve */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-gray-900 mb-8">
            À qui s'adresse URBA-2C ?
          </h2>

          {/* Particuliers + Carousel Simulations */}
          <div className="grid lg:grid-cols-2 gap-12 items-start mb-4">
            <div className="bg-white p-6 rounded-xl border-2 border-pink-100 hover:border-pink-300 transition-colors duration-300">
              <h3 className="text-2xl font-semibold text-pink-600 mb-3">Particuliers</h3>
              <div className="text-gray-600 leading-relaxed space-y-3">
                <p>Vous avez un projet de construction, de rénovation ou d'amélioration de votre logement ?</p>
                <p>Vous souhaitez voir la simulation de votre projet ?</p>
                <p>Vous êtes bloqué par les démarches administratives ou le montage de votre dossier pour une autorisation d'urbanisme ?</p>
                <p className="text-gray-700 font-medium">Je vous propose de découvrir votre projet grâce à la conception graphique.</p>
              </div>
            </div>
            <div className="flex flex-col">
              <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">Différentes simulations</h3>
              <div className="relative rounded-xl shadow-xl overflow-hidden bg-gray-900 max-h-[280px]" data-testid="carousel-simulations">
                <img
                  src={carouselImages[currentSlide].src}
                  alt={carouselImages[currentSlide].alt}
                  className="w-full h-[250px] object-contain transition-opacity duration-500"
                />
                <button onClick={prevSlide} className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-1.5 rounded-full shadow-lg transition-all duration-200" data-testid="carousel-prev">
                  <ChevronLeft className="w-5 h-5 text-gray-800" />
                </button>
                <button onClick={nextSlide} className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-1.5 rounded-full shadow-lg transition-all duration-200" data-testid="carousel-next">
                  <ChevronRight className="w-5 h-5 text-gray-800" />
                </button>
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
                  {carouselImages.map((_, idx) => (
                    <button key={idx} onClick={() => setCurrentSlide(idx)} className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${idx === currentSlide ? 'bg-pink-600 scale-125' : 'bg-white/60 hover:bg-white'}`} data-testid={`carousel-dot-${idx}`} />
                  ))}
                </div>
              </div>
              <p className="text-xs text-gray-500 text-center mt-2">{currentSlide + 1} / {carouselImages.length} — {carouselImages[currentSlide].alt}</p>
            </div>
          </div>

          {/* Bouton Demande de devis entre les carousels */}
          <div className="flex justify-center my-6">
            <Link to="/contact" className="inline-flex items-center px-8 py-3 bg-pink-600 hover:bg-pink-700 text-white text-lg font-semibold rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105" data-testid="devis-btn-between">
              DEMANDE DE DEVIS
            </Link>
          </div>

          {/* Professionnels + Carousel Dossier */}
          <div className="grid lg:grid-cols-2 gap-12 items-start mb-8">
            <div className="bg-white p-6 rounded-xl border-2 border-gray-200 hover:border-pink-300 transition-colors duration-300">
              <h3 className="text-2xl font-semibold text-pink-600 mb-3">Professionnels</h3>
              <div className="text-gray-600 leading-relaxed space-y-3">
                <p>Maçons, artisans de tous corps d'état du bâtiment, agents immobiliers... Je vous accompagne pour le montage de vos dossiers administratifs, techniques et graphiques pour les autorisations d'urbanisme de vos clients.</p>
                <p className="text-gray-700 font-semibold">Libre d'esprit, vous restez centré sur votre cœur de métier.</p>
              </div>
            </div>
            <div className="flex flex-col">
              <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">Dossier graphique</h3>
              <div className="relative rounded-xl shadow-xl overflow-hidden bg-white max-h-[280px] cursor-pointer" data-testid="carousel-dossier" onClick={() => setZoomedImage(dossierImages[currentDossier])}>
                <img
                  src={dossierImages[currentDossier].src}
                  alt={dossierImages[currentDossier].alt}
                  className="w-full h-[250px] object-contain transition-opacity duration-500"
                />
                <button onClick={(e) => { e.stopPropagation(); prevDossier(); }} className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-1.5 rounded-full shadow-lg transition-all duration-200" data-testid="dossier-prev">
                  <ChevronLeft className="w-5 h-5 text-gray-800" />
                </button>
                <button onClick={(e) => { e.stopPropagation(); nextDossier(); }} className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-1.5 rounded-full shadow-lg transition-all duration-200" data-testid="dossier-next">
                  <ChevronRight className="w-5 h-5 text-gray-800" />
                </button>
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
                  {dossierImages.map((_, idx) => (
                    <button key={idx} onClick={(e) => { e.stopPropagation(); setCurrentDossier(idx); }} className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${idx === currentDossier ? 'bg-pink-600 scale-125' : 'bg-white/60 hover:bg-white'}`} data-testid={`dossier-dot-${idx}`} />
                  ))}
                </div>
              </div>
              <p className="text-xs text-gray-500 text-center mt-2">{currentDossier + 1} / {dossierImages.length} — {dossierImages[currentDossier].alt}</p>
            </div>
          </div>

          {/* Message commun */}
          <div className="bg-pink-50 p-6 rounded-xl border-2 border-pink-200 mb-8">
            <p className="text-gray-800 leading-relaxed font-medium">
              Je me charge, à votre place, de la gestion complète du dossier avec toutes les formalités administratives et de les transmettre soit en mairie soit sur la plateforme GNAU (Guichet Numérique des Autorisations d'Urbanisme) avec l'assurance de leur validation auprès des services instructeurs (mairies, services d'urbanisme, ABF...)
            </p>
          </div>

          <div className="p-6 bg-pink-50 rounded-xl border-2 border-pink-200">
            <p className="text-lg text-gray-800 font-medium mb-4">
              N'hésitez pas à me contacter <Link to="/contact" className="text-pink-600 hover:text-pink-700 underline font-semibold">pour demander un devis</Link> :
            </p>
            <div className="space-y-2">
              <a href="tel:0761926962" className="block text-2xl font-bold text-pink-600 hover:text-pink-700 transition-colors">
                07.61.92.69.62
              </a>
              <a href="mailto:contact@urba2c.fr" className="block text-lg text-gray-700 hover:text-pink-600 transition-colors">
                contact@urba2c.fr
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-3">
              Mon accompagnement
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              De l'idée à la réalisation, je vous accompagne à chaque étape
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <Card className="border-2 border-gray-200 hover:border-pink-300 hover:shadow-xl transition-all duration-300">
              <CardContent className="p-8">
                <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center mb-6 mx-auto">
                  <span className="text-2xl font-bold text-pink-600">1</span>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center">Conseil</h3>
                <p className="text-gray-600 text-center">
                  Je vous conseille selon vos envies et exigences pour définir au mieux votre projet
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-gray-200 hover:border-pink-300 hover:shadow-xl transition-all duration-300">
              <CardContent className="p-8">
                <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center mb-6 mx-auto">
                  <span className="text-2xl font-bold text-pink-600">2</span>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center">Conception</h3>
                <p className="text-gray-600 text-center">
                  Création de votre dossier administratif et technique (plans 2D/3D, insertions...)
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-gray-200 hover:border-pink-300 hover:shadow-xl transition-all duration-300">
              <CardContent className="p-8">
                <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center mb-6 mx-auto">
                  <span className="text-2xl font-bold text-pink-600">3</span>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center">Dépôt</h3>
                <p className="text-gray-600 text-center">
                  Dépôt de votre dossier en mairie ou sur le GNAU (Guichet Numérique des Autorisations d'Urbanisme)
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-gray-900 mb-6 text-center">
            25 ans d'expérience à votre service
          </h2>
          <div className="max-w-4xl mx-auto space-y-4 text-gray-600 leading-relaxed">
            <p>
              Après 25 ans à collaborer dans différents bureaux d'études du bâtiment et d'architectures, 
              et au sein du service bâtiments et patrimoine d'une collectivité, je souhaite aider les particuliers 
              ainsi que les professionnels du bâtiment.
            </p>
            <p>
              Mon expertise consiste au montage de dossiers administratifs et techniques pour les autorisations 
              d'urbanisme, avec un travail soigné et rapide.
            </p>
            <p className="font-semibold text-pink-600">
              Tout en assurant le respect des règles d'urbanisme concernant le lieu du projet, vous avez une 
              garantie très forte sur l'acceptation et la validation de votre projet par la collectivité.
            </p>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Mes valeurs</h2>
            <p className="text-xl text-gray-300">Les principes qui guident mon travail au quotidien</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-pink-600 rounded-full flex items-center justify-center mb-6 mx-auto">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{value.title}</h3>
                  <p className="text-gray-400">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Modal Zoom Dossier */}
      {zoomedImage && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setZoomedImage(null)} data-testid="zoom-modal">
          <button onClick={() => setZoomedImage(null)} className="absolute top-6 right-6 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg z-10" data-testid="zoom-close">
            <X className="w-6 h-6 text-gray-800" />
          </button>
          <img
            src={zoomedImage.src}
            alt={zoomedImage.alt}
            className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default Presentation;